import * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * Single-value slider, built on a native `<input type="range">`.
 *
 * Deliberately NOT `@radix-ui/react-slider`: that package is not among this project's Radix
 * dependencies, and one control does not justify adding one. The native input is already keyboard
 * operable (arrows, Home/End, Page Up/Down), announces itself correctly to screen readers, and
 * honours `prefers-reduced-motion` for free — everything a Radix wrapper would re-implement.
 *
 * ⚠️ Do NOT re-introduce `appearance-none` + `::-webkit-slider-runnable-track` / `::-moz-range-thumb`
 * styling here. That is what the first version did, and it rendered the control INVISIBLE — two grey
 * stubs and no thumb — which no type-check or test could have caught (found by screenshotting the
 * running app against the prototype). The prototype styles this control with exactly one property,
 * `accent-color`, and lets the platform draw the track and thumb. That is correct in every browser
 * and every theme for free, and it is what we do.
 *
 * Always pair with a visible `<label htmlFor>` and, where the value has consequences, a live echo
 * of what the number means — a bare 0–25 is not self-explanatory.
 */
export interface SliderProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange'> {
  value: number;
  onValueChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
}

const Slider = React.forwardRef<HTMLInputElement, SliderProps>(
  ({ className, value, onValueChange, min = 0, max = 100, step = 1, ...props }, ref) => (
    <input
      ref={ref}
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onValueChange(Number(e.target.value))}
      // `accent-color` is the whole styling story — see the note above before changing this.
      style={{ accentColor: 'hsl(var(--primary))' }}
      className={cn(
        'w-full cursor-pointer',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className
      )}
      {...props}
    />
  )
);
Slider.displayName = 'Slider';

export { Slider };
